/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useState } from "react";
import Overview from "./Overview";
import AlarmOverview from "../AlarmOverview/AlarmOverview";
import Capacity from "../Capacity/Capacity";
import ExternalPerformance from "../ExternalPerformance/ExternalPerformance";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

interface TabItem {
  value: string;
  label: string;
  component: React.ReactNode;
  contentClassName?: string;
}


const Dashboard: React.FC = () => {
  const alarmsGridId = "alarm-overview-alarms";
  const [activeTab, setActiveTab] = useState("network-overview");
  const [selectedAlarmSeverity, setSelectedAlarmSeverity] = useState<string | undefined>(undefined);

  const handleAlarmSelect = useCallback((severity: string) => {
    setActiveTab("alarms");
    setSelectedAlarmSeverity(severity);
  }, []);

  const tabItems: TabItem[] = [
    {
      value: "network-overview",
      label: "Network Overview",
      component: <Overview onAlarmSeveritySelect={handleAlarmSelect} />,
      contentClassName: "pt-4 px-0",
    },
    {
      value: "alarms",
      label: "Alarms",
      component: (
        <AlarmOverview
          alarmsGridId={alarmsGridId}
          selectedSeverity={selectedAlarmSeverity}
          onSelectedSeverityHandled={() => setSelectedAlarmSeverity(undefined)}
        />
      ),
      contentClassName: "pt-0 px-0",
    },
    {
      value: "network-performance",
      label: "Network Performance",
      component: <ExternalPerformance />,
      contentClassName: "pt-0 px-0",
    },
    {
      value: "capacity",
      label: "Capacity",
      component: <Capacity />,
      contentClassName: "pt-4 px-0",
    },
  ];

  return (
    <div className="flex flex-col min-h-full" style={{ overflowY: "auto", backgroundColor: "var(--color-background)" }}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between px-4 md:px-8 pt-6">
          <TabsList className="bg-muted text-muted-foreground rounded-lg px-1 py-1 h-auto gap-0.5">
            {tabItems.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-md px-3 py-1.5 text-[0.875rem] font-medium border-0 bg-muted text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {tabItems.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className={tab.contentClassName} style={{ backgroundColor: "var(--color-background)" }}>
            {tab.component}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default Dashboard;
